 /**
  * GOnX Module Store - Registry de Módulos
  * 
  * Define todos os módulos disponíveis no sistema, suas rotas,
  * ícones e quais itens de menu cada módulo controla.
  * 
  * CORE: Funcionalidades sempre ativas (Auth, Clientes, Vendas Simples, Financeiro Básico)
  * ADD-ONS: Módulos toggleable por empresa
  */
 
 import { 
   Factory, 
   CreditCard, 
   BotMessageSquare, 
   Monitor, 
   DollarSign, 
   Truck,
   type LucideIcon 
 } from 'lucide-react';
 
 // Tipos de módulos disponíveis
 export type ModuleId = 
   | 'MODULE_PRODUCTION'
   | 'MODULE_RENTALS'
   | 'MODULE_CRM_AI'
   | 'MODULE_POS'
   | 'MODULE_FINANCIAL_ADVANCED'
   | 'MODULE_LOGISTICS_ADVANCED';
 
 export interface ModuleRoute {
   path: string;
   menuId?: string;
 }
 
export interface ModuleDefinition {
  id: ModuleId;
  name: string;
  shortName: string;
  description: string;
  icon: LucideIcon;
  color: string;
  routes: ModuleRoute[];
  menuItems: string[];
  features: string[];
  price?: number;
  isPopular?: boolean;
  stripePriceId?: string;
}
 
 /**
  * Registry central de todos os módulos do sistema
  */
 export const MODULES_REGISTRY: Record<ModuleId, ModuleDefinition> = {
   MODULE_PRODUCTION: {
     id: 'MODULE_PRODUCTION',
     name: 'Produção Industrial',
     shortName: 'Produção',
     description: 'PCP, Ordens de Produção, Fichas Técnicas e Controle de Qualidade',
     icon: Factory,
     color: 'bg-violet-500/10 text-violet-600 dark:text-violet-400',
     routes: [
       { path: 'production', menuId: 'production-menu' },
       { path: 'production-projects' },
       { path: 'production-orders' },
       { path: 'production-items' },
       { path: 'production-qc' },
       { path: 'production-operators' },
       { path: 'production-work-logs' },
       { path: 'production-reports' },
       { path: 'production-costs' },
       { path: 'production-engineering' },
       { path: 'production-kanban' },
     ],
     menuItems: ['production-menu'],
     features: [
       'Fichas Técnicas (BOM)',
       'Ordens de Produção',
       'Kanban Visual',
       'Controle de Qualidade',
       'Apontamento de Horas',
       'Custos de Produção',
    ],
    price: 149,
    isPopular: true,
    stripePriceId: 'price_1SxSUkCguV2vasl2owz9o9Ic',
   },
 
   MODULE_RENTALS: {
     id: 'MODULE_RENTALS',
     name: 'Locações e Cilindros',
     shortName: 'Locações',
     description: 'Gestão de contratos de locação, cilindros e ativos',
     icon: CreditCard,
     color: 'bg-purple-500/10 text-purple-600 dark:text-purple-400',
     routes: [
       { path: 'rentals', menuId: 'rentals' },
       { path: 'rental-assets' },
       { path: 'rental-requests' },
       // CONSIGNACAO ato 1 (I.180; pacote, decisão 1): "loans" (Romaneios › Saídas) passa ao básico, fora do módulo
       { path: 'logistics-cylinder-analysis' },
     ],
     menuItems: ['rentals'],
     features: [
       'Contratos de Locação',
       'Gestão de Ativos',
       'Pedidos de Locação',
       'Romaneios e Saídas',
       'Rastreio de Cilindros',
     ],
      price: 99,
      stripePriceId: 'price_1SxSVACguV2vasl22sDWYSXp',
    },
 
   MODULE_CRM_AI: {
     id: 'MODULE_CRM_AI',
     name: 'CRM Inteligente',
     shortName: 'CRM IA',
     description: 'Pipelines de vendas, Assistente IA e Metas comerciais',
     icon: BotMessageSquare,
     color: 'bg-pink-500/10 text-pink-600 dark:text-pink-400',
     routes: [
       { path: 'crm-sales-assistant', menuId: 'crm-sales-assistant' },
       { path: 'crm-routine', menuId: 'crm-routine' },
     ],
     menuItems: ['crm-sales-assistant', 'crm-routine'],
     features: [
       'Assistente de Vendas com IA',
       'Roteiro de Atendimento',
       'Metas e Objetivos',
       'Análise de Ciclo de Compra',
       'Sugestões Inteligentes',
      ],
      price: 79,
      isPopular: true,
      stripePriceId: 'price_1SxSVrCguV2vasl2Q0yT14ky',
    },
 
   MODULE_POS: {
     id: 'MODULE_POS',
     name: 'Ponto de Venda',
     shortName: 'PDV',
     description: 'Frente de caixa, TEF integrado e Smart POS',
     icon: Monitor,
     color: 'bg-orange-500/10 text-orange-600 dark:text-orange-400',
     routes: [
       { path: 'pos-mode', menuId: 'pos-mode' },
     ],
     menuItems: ['pos-mode'],
     features: [
       'Frente de Caixa Completa',
       'Integração TEF',
       'Smart POS (Cielo, Stone)',
       'Controle de Caixa',
       'NFC-e Consumidor',
     ],
      price: 119,
      stripePriceId: 'price_1SxSWNCguV2vasl2ZfgdjdyP',
    },
 
   MODULE_FINANCIAL_ADVANCED: {
     id: 'MODULE_FINANCIAL_ADVANCED',
     name: 'Financeiro Avançado',
     shortName: 'Fin. Avançado',
     description: 'DRE Gerencial, Centro de Custos, Cobrança e Conciliação',
     icon: DollarSign,
     color: 'bg-emerald-500/10 text-emerald-600 dark:text-emerald-400',
     routes: [
       { path: 'dre-management', menuId: 'dre-management' },
       { path: 'dre-configuration' },
       { path: 'financial-collections', menuId: 'financial-collections' },
       { path: 'financial-reconciliation', menuId: 'financial-reconciliation' },
       { path: 'financial-cashflow', menuId: 'financial-cashflow' },
       { path: 'financial-cost-centers' },
     ],
     menuItems: [
       'dre-management', 
       'financial-collections', 
       'financial-reconciliation',
       'financial-cashflow',
     ],
     features: [
       'DRE Gerencial',
       'Centro de Custos',
       'Cobrança Automatizada',
       'Conciliação Bancária',
       'Fluxo de Caixa Projetado',
     ],
      price: 129,
      stripePriceId: 'price_1SxSWxCguV2vasl2QPFzgqCO',
    },
 
   MODULE_LOGISTICS_ADVANCED: {
     id: 'MODULE_LOGISTICS_ADVANCED',
     name: 'Logística Avançada',
     shortName: 'Logística+',
     description: 'Roteirização, App Motorista e Rastreamento em tempo real',
     icon: Truck,
     color: 'bg-cyan-500/10 text-cyan-600 dark:text-cyan-400',
     routes: [
       { path: 'driver-mode' },
       { path: 'logistics-technicians' },
       { path: 'logistics-fuel' },
     ],
     menuItems: ['driver-mode', 'logistics-technicians', 'logistics-fuel'],
     features: [
       'App do Motorista',
       'Roteirização Inteligente',
       'Rastreamento GPS',
       'Controle de Combustível',
       'Gestão de Técnicos',
     ],
      price: 89,
      stripePriceId: 'price_1SxSXPCguV2vasl2F1EOesM8',
    },
  };
 
 /**
  * Retorna todos os IDs de módulos disponíveis
  */
 export const getAllModuleIds = (): ModuleId[] => 
   Object.keys(MODULES_REGISTRY) as ModuleId[];
 
 /**
  * Verifica se uma rota pertence a algum módulo específico
  * @returns O ID do módulo ou null se for CORE
  */
 export const getModuleForRoute = (routePath: string): ModuleId | null => {
   for (const [moduleId, definition] of Object.entries(MODULES_REGISTRY)) {
     if (definition.routes.some(r => routePath.startsWith(r.path))) {
       return moduleId as ModuleId;
     }
   }
   return null;
 };
 
 /**
  * Verifica se um item de menu pertence a algum módulo específico
  * @returns O ID do módulo ou null se for CORE
  */
 export const getModuleForMenuItem = (menuItemId: string): ModuleId | null => {
   for (const [moduleId, definition] of Object.entries(MODULES_REGISTRY)) {
     if (definition.menuItems.includes(menuItemId)) {
       return moduleId as ModuleId;
     }
   }
   return null;
 };
 
 /**
  * Lista de funcionalidades CORE (sempre ativas)
  */
 export const CORE_FEATURES = [
   'Auth/Login',
   'Clientes (dados básicos)',
   'Carteira de Clientes',
   'Mapa de Clientes',
   'Vendas/Orçamentos',
   'Notas Fiscais (NF-e)',
   'Contas a Receber',
   'Contas a Pagar',
   'Cadastro Produtos',
   'Estoque Básico',
  'Tarefas',
] as const;

/**
 * Bundle "All Access" - Acesso a todos os módulos
 */
export const BUNDLE_ENTERPRISE = {
  id: 'BUNDLE_ENTERPRISE',
  name: 'GOnX Enterprise',
  description: 'Acesso ilimitado a todos os módulos: Produção, Locação, CRM IA, PDV, Financeiro Avançado e Logística.',
  originalPrice: 699,
  price: 499,
  stripePriceId: 'price_1SxSTiCguV2vasl2z0vH64GR',
} as const;